import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Card Component - Design System Atmosférico v3.0
 * 
 * Geometria Bold (24px) + Gradient Depth
 * Levitação em hover + Névoa dourada no topo
 */
const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-bold border border-void-warm bg-gradient-to-br from-void-warm to-void-deep text-textPrimary shadow-z1",
        "transition-all duration-flow ease-decelerate",
        "hover:shadow-z3 hover:-translate-y-1",
        className
      )}
      {...props}
    />
  )
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  )
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-headline leading-none tracking-tight", className)}
      {...props}
    />
  )
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-caption text-textSecondary", className)} {...props} />
  )
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  )
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  )
);
CardFooter.displayName = "CardFooter";

/**
 * ProjectCard - Para templates e previews (9:16)
 * Com névoa dourada no topo e levitação
 */
const ProjectCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    isActive?: boolean;
    aspectRatio?: "vertical" | "horizontal";
  }
>(({ className, isActive = false, aspectRatio = "vertical", ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "relative overflow-hidden rounded-bold bg-gradient-to-br from-void-warm to-void-deep shadow-z1",
      "transition-all duration-flow ease-decelerate",
      "hover:shadow-z3 hover:-translate-y-2",
      aspectRatio === "vertical" ? "aspect-vertical" : "aspect-horizontal",
      isActive && "ring-2 ring-gold-primary animate-aura-breathe",
      className
    )}
    {...props}
  >
    {/* Névoa dourada no topo */}
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-2/5"
      style={{
        background: "linear-gradient(180deg, rgba(255,215,0,0) 0%, rgba(255,215,0,0.04) 50%, rgba(255,215,0,0.10) 100%)"
      }}
    />
    {props.children}
  </div>
));
ProjectCard.displayName = "ProjectCard";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, ProjectCard };
